import moment from "moment";
import { useState, Fragment } from "react";

import { Chip, Tooltip } from "@mui/material";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import conf from "config-dlm";
import HSMRow from "./HSM";
import HostRow from "./Host";
import BaseRow from "./Base";
import LogsRow from "./Logs";
import Spinner from "components/utils/Spinner";
import ErrorChip from "components/utils/ErrorChip";
import ServiceRow from "./Services";
import ResourceRow from "./Resources";
import SuccessChip from "components/utils/SuccessChip";
import { useAuth } from "components/auth/AuthProvider";
import { useSmartContent } from "components/utils/SmartCell";
import DescriptionWithEdit from "components/projects/Description";
import { ProjectConfigSchema, usePavmLive } from "hooks/pavm/base";
import { usePavmFwupdateStatus, usePavmFwupdates } from "hooks/pavm/fwupdate";

const Overview = ({ config }: { config: ProjectConfigSchema }) => {
  const auth = useAuth();

  const host = config.host;
  const latest = config.latest;
  const project = auth.user.project.name;

  const [open, setOpen] = useState(false);
  const toggleOpen = () => setOpen(!open);

  const live = usePavmLive(host);
  const error = live?.error?.response?.data?.message;

  // create current, single 'latest until live' data structure to populate all host data while fetching
  const current = live.data?.data ?? latest?.data;

  const updatedAt = live.isSuccess ? live?.data?.updatedAt : latest?.updatedAt;

  const { data: releases } = usePavmFwupdates();
  const { data: release } = usePavmFwupdateStatus(host);

  // if lastModified date on installed release is < lastModified date of any current release, we know there is a new release available for install
  const installedLastModified = release?.installed?.lastModified;
  const updateAvailable = releases?.some((release) =>
    moment(release.lastModified).isAfter(moment(installedLastModified))
  );

  // if installed.lastModified not found, show GRACE version; else, check if update available (date based) or if apppliance is running latest
  const Tag = !installedLastModified ? (
    current?.meta?.version
  ) : updateAvailable ? (
    <Chip color="success" label="Update Available!" />
  ) : (
    <Tooltip title={current.meta.version}>
      <i>latest</i>
    </Tooltip>
  );

  const Role =
    conf.HA_ROLE?.[current?.base?.role?.data as keyof typeof conf.HA_ROLE];
  const Ip = useSmartContent(current?.base?.ip?.data);
  const SerialNumber = useSmartContent(current?.base?.serialNumber?.data);

  const modified = {
    meta: current?.meta,
    clock: current?.base.clock,
    kernel: current?.base.kernel,
  };

  return (
    <Fragment>
      <TableRow sx={conf.LAST_CHILD_NO_BORDER}>
        <Fragment>
          <TableCell>
            <IconButton
              size="small"
              onClick={toggleOpen}
              disabled={!current}
              aria-label="toggle detail row"
              sx={{
                transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </TableCell>

          <TableCell>
            <b>{host.toUpperCase()}</b>

            <DescriptionWithEdit
              id={config.id}
              type="appliance"
              isAdmin={true}
              description={config.description}
            />
          </TableCell>

          <TableCell align="right">
            {live.isLoading && <Spinner />}
            {live.isError && <ErrorChip info={error} />}
            {live.isSuccess && <SuccessChip info="Connected" />}
          </TableCell>

          <TableCell align="right">{Ip ?? "--"}</TableCell>

          <TableCell align="right">{SerialNumber}</TableCell>

          <TableCell align="right">{Tag ?? "--"}</TableCell>

          <TableCell align="right">
            {Role ? (
              <Chip
                variant="filled"
                icon={Role.icon}
                label={Role.title}
                color={Role.color}
              />
            ) : (
              "--"
            )}
          </TableCell>
        </Fragment>
      </TableRow>

      {open && (
        <BaseRow
          key={`${project}-base`}
          open={open}
          config={config}
          data={modified}
          version={current?.meta?.version}
          updatedAt={updatedAt}
        />
      )}

      {open && (
        <HostRow
          key={`${project}-host`}
          open={open}
          config={config}
          data={current?.base}
          version={current?.meta?.version}
          updatedAt={updatedAt}
        />
      )}

      {open && (
        <ResourceRow
          key={`${project}-resources`}
          open={open}
          config={config}
          data={current?.resources}
        />
      )}

      {open && (
        <HSMRow
          key={`${project}-hsm`}
          open={open}
          config={config}
          data={current?.hsm}
        />
      )}

      {open && (
        <ServiceRow
          key={`${project}-services`}
          open={open}
          config={config}
          data={current?.services ?? []}
          updatedAt={updatedAt}
        />
      )}

      {open && (
        <LogsRow
          key={`${project}-logs`}
          open={open}
          config={config}
          data={current?.logs}
          updatedAt={updatedAt}
        />
      )}
    </Fragment>
  );
};

export default Overview;
